import React from "react";
import { Box, Typography, Grid, Link } from "@mui/material";
import moment from "moment";

const LeadDetailBody = ({ leadData, projectList }) => {
  if (!leadData) return null;

  const { history, __v, _id, ...details } = leadData;

  const normalizedDetails = {};
  Object.entries(details)?.forEach(([key, value]) => {
    const lowerKey = key?.toLowerCase();
    if (!(lowerKey in normalizedDetails)) {
      normalizedDetails[lowerKey] = { originalKey: key, value };
    }
  });

  const isPdfField = (key) => /pdf/i.test(key);
  const isProjectField = (key) => /(project|projectid)/i.test(key);
  const isTestField = (key) => /(test|exam)/i.test(key);
  const isArrayOfObjects = (val) =>
    Array.isArray(val) && val.length > 0 && typeof val[0] === "object";

  const createLabel = (key) => {
    if (isProjectField(key)) return "Project Name";
    return key
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase())
      .trim();
  };

  const formatValue = (val, key) => {
    if (val == null || val === "") return "-";

    if (isPdfField(key) && Array.isArray(val)) {
      return (
        <Box>
          {val?.map((url, index) => (
            <Link
              key={index}
              href={url}
              target="_blank"
              download
              sx={{ display: "block", mb: 0.5 }}
            >
              Download PDF {index + 1}
            </Link>
          ))}
        </Box>
      );
    }

    if (isPdfField(key) && typeof val === "string" && val.startsWith("http")) {
      return (
        <Link href={val} target="_blank" rel="noopener noreferrer" download>
          Download PDF
        </Link>
      );
    }

    if (isProjectField(key) && typeof val === "string" && projectList) {
      const project = projectList?.find((p) => p?._id === val);
      return project ? project?.projectName : val;
    }

    if (isTestField(key)) {
      if (Array.isArray(val)) return val?.length > 0 ? val?.join(", ") : "-";
      if (typeof val === "string") {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) {
          return parsed?.length > 0
            ? parsed
                ?.map((item) =>
                  typeof item === "object"
                    ? item.response || item.name || JSON.stringify(item)
                    : item
                )
                .join(", ")
            : "-";
        }
      }
      return val.toString();
    }

    if (/(date|at|time)$/i.test(key)) {
      const momentDate = moment(val);
      if (momentDate.isValid()) {
        return momentDate.format("MMM DD, YYYY hh:mm A");
      }
    }

    if (isArrayOfObjects(val)) {
      return (
        <Box component="ul" sx={{ m: 0, pl: 0.5 }}>
          {val.map((item, index) => (
            <li key={index}>
              <Typography className="font-13-grey">
                {typeof item === "object"
                  ? Object.entries(item)
                      ?.map(([k, v]) => `${k}: ${v}`)
                      ?.join(", ")
                  : item?.toString()}
              </Typography>
            </li>
          ))}
        </Box>
      );
    }

    if (Array.isArray(val)) return val?.length > 0 ? val.join(", ") : "-";

    if (
      typeof val === "string" &&
      (val.startsWith("[") || val.startsWith("{"))
    ) {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) {
        if (parsed?.length === 0) return "--";
        if (typeof parsed[0] === "object") {
          return (
            <Box component="ul" sx={{ m: 0, pl: 0.5 }}>
              {parsed.map((item, index) => (
                <li key={index}>
                  <Typography className="font-13-grey">
                    {Object.entries(item)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(", ")}
                  </Typography>
                </li>
              ))}
            </Box>
          );
        }
        return parsed?.join(", ");
      }
      return JSON.stringify(parsed);
    }

    if (typeof val === "object") return JSON.stringify(val);

    return val.toString();
  };

  return (
    <Box sx={{ maxHeight: "70vh", overflowY: "auto", p: 2 }}>
      <Grid container spacing={2}>
        {(Object.values(normalizedDetails) as any[]).map(
          ({ originalKey, value }: any, index: number) => (
            <Grid size={6} key={index}>
              <Typography className="font-14-600-grey">
                {createLabel(originalKey)}
              </Typography>
              <Typography className="font-13-grey">
                {formatValue(value, originalKey)}
              </Typography>
            </Grid>
          )
        )}
      </Grid>
    </Box>
  );
};

export default LeadDetailBody;
